import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import useScrollToTop from '@/hooks/useScrollToTop';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://pandabuy.blog';

interface BlogPostLayoutProps {
  title: string;
  description: string;
  date: string;
  readTime: string;
  featuredImage: string;
  featuredImageAlt: string;
  tag: string;
  slug: string;
  children: React.ReactNode;
  faqs?: Array<{ q: string; a: string }>;
}

export default function BlogPostLayout({
  title,
  description,
  date,
  readTime,
  featuredImage,
  featuredImageAlt,
  tag,
  slug,
  children,
  faqs,
}: BlogPostLayoutProps) {
  useScrollToTop();

  const pageUrl = `${SITE_URL}/blog/${slug}`;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `${pageUrl}#article`,
        headline: title,
        description: description,
        datePublished: date,
        image: featuredImage,
        author: {
          '@type': 'Organization',
          '@id': `${SITE_URL}/#organization`,
          name: 'Pandabuy Blog',
          url: `${SITE_URL}/`,
        },
        publisher: {
          '@type': 'Organization',
          '@id': `${SITE_URL}/#organization`,
          name: 'Pandabuy Blog',
          url: `${SITE_URL}/`,
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': pageUrl,
        },
      },
      ...(faqs && faqs.length > 0
        ? [
            {
              '@type': 'FAQPage',
              '@id': `${pageUrl}#faq`,
              mainEntity: faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.q,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.a,
                },
              })),
            },
          ]
        : []),
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />
      <Navbar />
      <main>
        <article className="pt-24 pb-16 md:pb-24">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <nav className="mb-8" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-sm text-foreground-500">
                <li>
                  <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">Home</Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
                </li>
                <li>
                  <span className="text-foreground-700">{tag}</span>
                </li>
              </ol>
            </nav>

            <header className="mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-4">
                {tag}
              </span>
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-900 leading-tight">
                {title}
              </h1>
              <div className="mt-4 flex items-center gap-4 text-xs text-foreground-500">
                <span>Updated: {date}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{readTime}</span>
              </div>
            </header>

            <div className="relative w-full h-64 md:h-96 rounded-2xl overflow-hidden mb-10">
              <img
                src={featuredImage}
                alt={featuredImageAlt}
                title={featuredImageAlt}
                className="w-full h-full object-cover object-top rounded-2xl"
              />
            </div>

            <div className="prose-custom space-y-6 text-foreground-700 leading-relaxed">
              {children}
            </div>

            {faqs && faqs.length > 0 && (
              <div className="mt-12">
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-6">Frequently Asked Questions</h2>
                <div className="space-y-6">
                  {faqs.map((faq, idx) => (
                    <div key={idx}>
                      <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">
                        {faq.q}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-12 p-6 md:p-8 rounded-2xl bg-foreground-900 text-center">
              <h3 className="text-xl font-heading font-bold text-background-50 mb-3">
                Ready to Explore the Pandabuyspreadsheet Database?
              </h3>
              <p className="text-sm text-foreground-300 mb-6 max-w-md mx-auto">
                Browse all 14 curated categories with verified product links, quality scores, and the latest trending finds.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/"
                  className="px-6 py-3 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  Back to Homepage
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 rounded-full border border-background-50/30 text-background-50 text-sm hover:bg-background-50/10 transition-all whitespace-nowrap cursor-pointer"
                >
                  Open Pandabuy Sheet &rarr;
                </a>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs text-foreground-500">Related:</span>
                <Link to="/" className="text-xs text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
                  Pandabuyspreadsheet Categories
                </Link>
                <span className="text-foreground-300">&middot;</span>
                <Link to="/" className="text-xs text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
                  Best Pandabuy Finds 2026
                </Link>
                <span className="text-foreground-300">&middot;</span>
                <Link to="/" className="text-xs text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
                  Pandabuy Haul Tips
                </Link>
              </div>
            </div>
          </div>
        </article>

        <style>{`
          .prose-custom p {
            font-size: 0.9375rem;
            line-height: 1.8;
          }
          .prose-custom strong {
            color: oklch(var(--foreground-900));
          }
          .prose-custom h2 {
            font-size: 1.5rem;
            line-height: 1.3;
            margin-top: 2.5rem;
            margin-bottom: 1rem;
            font-family: var(--font-heading);
            font-weight: 700;
            color: oklch(var(--foreground-900));
          }
          .prose-custom h3 {
            font-size: 1.25rem;
            line-height: 1.3;
            margin-top: 2rem;
            margin-bottom: 0.75rem;
            font-family: var(--font-heading);
            font-weight: 600;
            color: oklch(var(--foreground-900));
          }
          .prose-custom ul {
            list-style-type: disc;
            padding-left: 1.5rem;
            margin-bottom: 1rem;
          }
          .prose-custom ul li {
            margin-bottom: 0.5rem;
          }
          .prose-custom a {
            color: oklch(var(--primary-600));
            text-decoration: underline;
          }
          .prose-custom a:hover {
            color: oklch(var(--primary-700));
          }
        `}</style>
      </main>
      <Footer />
    </>
  );
}